import { captions } from './helpers'
import {
  px,
  type BlueprintItem,
  type BlueprintSolid,
  type TemplateBlueprint,
} from './types'
import type { Orientation } from '../../types'

/**
 * `VideoLayout` (`layouts/VideoLayout.tsx`) and its three skins: the video
 * fills the canvas (the scene items already do), captions sit low — on a
 * black strip (`CaptionsLayout`), bare and uppercase in a display face
 * (`MemeLayout`), or on a translucent rounded strip (`TranslucentLayout`).
 * One caption line. The video letterboxes over a blurred copy of itself
 * as v1's did, through the scene item's own fit (Auto, #547: `../fit.tsx`);
 * #552's video slots reuse the per-item fit and backdrop. Meme's
 * captions are Anton, the catalogue's stand-in for Impact, which has no
 * loader (`layoutDefaults.ts`). Intended difference (#547): in square and
 * landscape the captions (and strip) sit 70 px higher, clear of the
 * watermark.
 */

/** The caption text box: v1's container (`px-[120px] pb-[400px]` / `p-[60px]`) minus its 20 px padding. */
const CAPTIONS: Record<Orientation, [number, number, number, number]> = {
  portrait: [140, 1410, 800, 90],
  square: [80, 840, 920, 90],
  landscape: [80, 840, 1760, 90],
}

const MEME_CAPTIONS: Record<Orientation, [number, number, number, number]> = {
  portrait: [120, 1370, 840, 150],
  square: [60, 800, 960, 150],
  landscape: [60, 800, 1800, 150],
}

/** The strip is the text box padded by 20 px on every side (`p-5`). */
function strip(
  o: Orientation,
  extra: Partial<Pick<BlueprintSolid, 'opacity' | 'borderRadius'>> = {},
): BlueprintSolid {
  const [x, y, w, h] = CAPTIONS[o]
  return {
    id: 'captionsStrip',
    type: 'solid',
    box: px(o, x - 20, y - 20, w + 40, h + 40),
    z: 39,
    color: '#000000',
    ...extra,
  }
}

function skin(make: (o: Orientation) => BlueprintItem[]): TemplateBlueprint {
  return {
    kind: 'video',
    orientations: {
      portrait: make('portrait'),
      square: make('square'),
      landscape: make('landscape'),
    },
  }
}

export const captionsBlueprint = skin((o) => [
  strip(o),
  captions(o, CAPTIONS[o], 'end'),
])

export const memeBlueprint = skin((o) => [
  {
    ...captions(o, MEME_CAPTIONS[o], 'end'),
    captions: {
      typography: 'layout',
      align: 'end',
      textTransform: 'uppercase',
    },
  },
])

export const translucentBlueprint = skin((o) => [
  strip(o, {
    opacity: 0.4,
    borderRadius: 24 / (o === 'landscape' ? 1920 : 1080),
  }),
  captions(o, CAPTIONS[o], 'end'),
])
